import type { PaymentMilestoneData } from '@/lib/api'
import { isMilestoneSettled } from '@/lib/payment-milestone-display'
import { calculateMilestoneAmounts } from '@/lib/payment-milestone-allocation'
import { PaymentMilestoneStatus, QuoteStateCode, QuoteStatusCode, QuoteType } from '@/lib/types'
import Decimal from 'decimal.js'

export type PaymentMilestoneProjection = {
  eligible: boolean
  milestones: PaymentMilestoneData[]
  changedIds: string[]
  protectedRowCount: number
}

export function projectPaymentMilestones({
  milestones,
  quoteType,
  quoteStateCode,
  quoteStatusCode,
  total,
}: {
  milestones: PaymentMilestoneData[]
  quoteType: QuoteType | number | undefined
  quoteStateCode: QuoteStateCode | number | undefined
  quoteStatusCode?: QuoteStatusCode | number
  total: number
}): PaymentMilestoneProjection {
  const activeMilestones = milestones.filter((milestone) => milestone.statuscode !== PaymentMilestoneStatus.Inactive)
  const protectedRowCount = activeMilestones.filter(
    (milestone) => milestone.dsy_percent_was_null || !!milestone.dsy_paylink || isMilestoneSettled(milestone)
  ).length
  const allocatedPercent = activeMilestones.reduce(
    (sum, milestone) => sum.add(milestone.dsy_percent),
    new Decimal(0)
  )
  const allocationValid =
    quoteType === QuoteType.Budget ? allocatedPercent.lessThanOrEqualTo(100) : allocatedPercent.equals(100)
  // Activation validates milestone amounts against the quote total and locks
  // them; from then on the stored dsy_amount is the billing truth and must be
  // displayed verbatim. Only Draft schedules track the live total via percents.
  const eligible =
    quoteStateCode === QuoteStateCode.Draft &&
    quoteStatusCode !== QuoteStatusCode.Draft_Rebasing &&
    activeMilestones.length > 0 &&
    protectedRowCount === 0 &&
    allocationValid

  if (!eligible) {
    return { eligible: false, milestones, changedIds: [], protectedRowCount }
  }

  const projectedActive = calculateMilestoneAmounts(activeMilestones, total)
  const projectedById = new Map(
    projectedActive.map((milestone) => [milestone.dsy_paymentmilestoneid, milestone.dsy_amount])
  )
  const changedIds: string[] = []
  const projected = milestones.map((milestone) => {
    const amount = projectedById.get(milestone.dsy_paymentmilestoneid)
    if (amount === undefined) return milestone
    if (!new Decimal(amount).toDecimalPlaces(2).equals(new Decimal(milestone.dsy_amount).toDecimalPlaces(2))) {
      changedIds.push(milestone.dsy_paymentmilestoneid)
    }
    return { ...milestone, dsy_amount: amount }
  })

  return { eligible: true, milestones: projected, changedIds, protectedRowCount }
}
